import { useCallback, useEffect, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { shortDateTime } from '../../shared/dateTime';
import { ClampedText } from '../../shared/listCells';
import { menuLabel } from '../menuConfig';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import {
  FEATURE_PUBLICATION_EXCEPTIONS,
  MIN_EXCEPTION_REASON_LENGTH,
  PUBLICATION_EXCEPTION_APPROVE_LABEL,
  approveException,
  fetchExceptionQueue,
  notifyPublicationExceptionsChanged,
  rejectException,
  withdrawException,
  type ExceptionQueueItem,
} from './publicationExceptionsApi';
import { ContractTestId } from '../../usage/ContractTestId';

/**
 * „Wnioski o wyjątek” — kolejka wniosków traderów o wyjątek od bramki
 * publikacji (makieta back office v2, fala B, 21.10.2026: BR-SAL-01 —
 * „wyjątek zatwierdza Sales albo opiekun klienta”, widok Sales „Akceptacje”;
 * ścieżki TRD-03 krok 4, MIX-03 krok 4; decyzja D9 użytkownika 20.10.2026;
 * przełącznik `publicationExceptionRequests`).
 *
 * Rozstrzyga osoba z uprawnieniem „Akceptacja wyjątku od zakazu
 * podwykonawcy” — rolą (administratorzy) albo imiennie na koncie; czy to
 * pytający, mówi serwer przy każdym wierszu. Trader widzi tu swoje wnioski
 * i może wycofać czekający. Domyślnie czekające, najnowsze pierwsze.
 */

export const FEATURE_OFF_NOTE = 'Ta funkcja jest wyłączona w wersji aplikacji, z której korzystasz.';

export const QUEUE_FILTERS: Array<{ id: string; name: string }> = [
  { id: 'pending', name: 'Czekające na decyzję' },
  { id: 'approved', name: 'Zatwierdzone' },
  { id: 'rejected', name: 'Odrzucone' },
  { id: 'expired', name: 'Nieaktualne' },
  { id: 'withdrawn', name: 'Wycofane' },
  { id: 'all', name: 'Wszystkie' },
];

export const statusColor = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' =>
  status === 'pending' ? 'warning' : status === 'approved' ? 'success' : status === 'rejected' ? 'error' : 'default';

/** „PL Kraków → AT Wien” — kraj i miasto, czego nie ma, pomijamy. */
export function routeText(order: ExceptionQueueItem['order']): string {
  const place = (country: string | null, city: string | null) =>
    [country, city].filter((x) => typeof x === 'string' && x.trim() !== '').join(' ') || '—';
  return `${place(order.fromCountry, order.fromCity)} → ${place(order.toCountry, order.toCity)}`;
}

/** Rozstrzygnięcie jednym zdaniem pod stanem — kto, kiedy, dlaczego. */
export function decisionText(r: ExceptionQueueItem): string | null {
  if (r.status === 'expired') return r.expiryReason;
  if (!r.decidedAt) return null;
  const by = r.decidedBy?.username ?? '—';
  const note = r.decisionReason ? `: ${r.decisionReason}` : '';
  return `${by}, ${shortDateTime(r.decidedAt)}${note}`;
}

type Decision = { kind: 'approve' | 'reject'; item: ExceptionQueueItem };

export const PublicationExceptionsList = () => {
  const on = useFeatureState(FEATURE_PUBLICATION_EXCEPTIONS);
  if (on === undefined) return <FeaturesLoading />;
  if (!on) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">{FEATURE_OFF_NOTE}</Alert>
      </Box>
    );
  }
  return <ExceptionsQueue />;
};

const ExceptionsQueue = () => {
  const notify = useNotify();
  const [filter, setFilter] = useState('pending');
  const [items, setItems] = useState<ExceptionQueueItem[] | null>(null);
  // `null` = jeszcze nie wiemy (przed pierwszą odpowiedzią) — wtedy bez zdania o uprawnieniu;
  // dotąd przez chwilę stało „Rozstrzyga osoba z uprawnieniem…” także u osoby uprawnionej
  // (lokalny e2e 22.10.2026, s17 czytał ekran przed odpowiedzią).
  const [canDecide, setCanDecide] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Decision | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetchExceptionQueue(filter);
      setItems(r.items);
      setCanDecide(r.canDecide);
      setError(null);
    } catch (e) {
      setItems([]);
      setError(toErrorMessage(e));
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  const close = () => {
    setDialog(null);
    setReason('');
  };

  const decide = async () => {
    if (!dialog) return;
    setBusy(true);
    try {
      if (dialog.kind === 'approve') {
        await approveException(dialog.item.id, reason.trim());
        notify('Wyjątek zatwierdzony — trader może zlecić publikację.', { type: 'success' });
      } else {
        await rejectException(dialog.item.id, reason.trim());
        notify('Wniosek o wyjątek odrzucony.', { type: 'success' });
      }
      close();
      notifyPublicationExceptionsChanged();
      await load();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (item: ExceptionQueueItem) => {
    setBusy(true);
    try {
      await withdrawException(item.id);
      notify('Wniosek o wyjątek wycofany.', { type: 'success' });
      notifyPublicationExceptionsChanged();
      await load();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const reasonTooShort = dialog?.kind === 'reject' && reason.trim().length < MIN_EXCEPTION_REASON_LENGTH;

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }} data-testid="publication-exceptions">
      <Title title={menuLabel('publication-exceptions')} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Wnioski traderów o wyjątek od reguły klienta przy publikacji na giełdzie — gdy klient zakazuje
        podwykonawców albo trader nie może potwierdzić warunków.{' '}
        {canDecide === null
          ? ''
          : canDecide
            ? 'Masz uprawnienie, żeby je zatwierdzać i odrzucać (własnych wniosków nie rozstrzygasz). '
            : `Rozstrzyga osoba z uprawnieniem „${PUBLICATION_EXCEPTION_APPROVE_LABEL}”; tu widzisz stan swoich wniosków. `}
        Zatwierdzony wniosek pozwala traderowi zlecić publikację; wygasa, gdy zlecenie zejdzie z Trade.
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="exceptions-filter">Pokaż</InputLabel>
          <Select
            labelId="exceptions-filter"
            label="Pokaż"
            value={filter}
            onChange={(e) => setFilter(String(e.target.value))}
            data-testid="exceptions-filter"
          >
            {QUEUE_FILTERS.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {items === null && <CircularProgress size={24} />}
      {items !== null && items.length === 0 && !error && (
        <Typography variant="body2" color="text.secondary" data-testid="exceptions-empty">
          Brak wniosków w tym widoku.
        </Typography>
      )}
      {items !== null && items.length > 0 && (
        <Table size="small" sx={{ '& .MuiTableCell-sizeSmall': { px: 1 } }}>
          <TableHead>
            <TableRow>
              <TableCell>Nr</TableCell>
              <TableCell>Zlecenie</TableCell>
              <TableCell>Klient i reguła</TableCell>
              <TableCell>Wnioskujący</TableCell>
              <TableCell>Powód</TableCell>
              <TableCell>Stan</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((r) => (
              <TableRow key={r.id} hover data-testid={`exception-row-${r.id}`}>
                <TableCell>{r.id}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <a href={`#/user-offers/${r.order.id}/show`}>{r.order.externalId ?? `#${r.order.id}`}</a>
                  <Typography variant="caption" component="div" color="text.secondary">
                    {routeText(r.order)}
                    {r.order.fromDate ? `, ${shortDateTime(r.order.fromDate)}` : ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  <ClampedText text={r.contractor?.legalName ?? '—'} maxWidth={{ xs: 160, xl: 260 }} />
                  <Typography variant="caption" component="div" color="text.secondary">
                    {r.ruleLabel}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {r.requestedBy?.username ?? '—'}
                  <Typography variant="caption" component="div" color="text.secondary">
                    {shortDateTime(r.createdAt)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <ClampedText text={r.reason} maxWidth={{ xs: 200, xl: 360 }} />
                </TableCell>
                <TableCell>
                  <Chip size="small" color={statusColor(r.status)} label={r.statusLabel} />
                  {decisionText(r) && (
                    <Typography variant="caption" component="div" color="text.secondary">
                      {decisionText(r)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {r.canDecide && (
                    <>
                      {/* Kontrakt `data-testid` paczki (S-22.10-02): zgoda na wyjątek. */}
                      <ContractTestId id="sal-akceptacje-zgoda" disabled={busy}>
                        <Button
                          size="small"
                          data-testid={`exception-approve-${r.id}`}
                          disabled={busy}
                          onClick={() => setDialog({ kind: 'approve', item: r })}
                        >
                          Zatwierdź
                        </Button>
                      </ContractTestId>
                      <Button
                        size="small"
                        color="error"
                        data-testid={`exception-reject-${r.id}`}
                        disabled={busy}
                        onClick={() => setDialog({ kind: 'reject', item: r })}
                      >
                        Odrzuć
                      </Button>
                    </>
                  )}
                  {r.mine && r.status === 'pending' && (
                    <Button
                      size="small"
                      data-testid={`exception-withdraw-${r.id}`}
                      disabled={busy}
                      onClick={() => void withdraw(r)}
                    >
                      Wycofaj
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialog !== null} onClose={() => !busy && close()} fullWidth maxWidth="sm">
        <DialogTitle>
          {dialog?.kind === 'approve' ? 'Zatwierdzić' : 'Odrzucić'} wniosek nr {dialog?.item.id}?
        </DialogTitle>
        <DialogContent>
          {dialog && (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Zlecenie {dialog.item.order.externalId ?? `#${dialog.item.order.id}`}, klient{' '}
              {dialog.item.contractor?.legalName ?? '—'} ({dialog.item.ruleLabel}). Powód wnioskującego:{' '}
              {dialog.item.reason}
            </Typography>
          )}
          <TextField
            label={
              dialog?.kind === 'approve' ? 'Uwagi (opcjonalnie)' : `Powód (co najmniej ${MIN_EXCEPTION_REASON_LENGTH} znaków)`
            }
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            slotProps={{ htmlInput: { 'data-testid': 'exception-decision-reason' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            color={dialog?.kind === 'reject' ? 'error' : 'primary'}
            data-testid="exception-decision-submit"
            disabled={busy || reasonTooShort}
            onClick={() => void decide()}
          >
            {dialog?.kind === 'approve' ? 'Zatwierdź' : 'Odrzuć'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
